import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { AlertBanner } from '../security/AlertBanner';
import { PoweredByFooter } from './PoweredByFooter';
import { AssistantWidget } from '../assistant/AssistantWidget';
import { useAssistantEnabled } from '../../hooks/useAssistantStatus';
import { tenantName } from '../../config/tenant';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Portfolio Dashboard',
  '/historian': 'Historian',
  '/admin/users': 'User Management',
  '/admin/settings': 'Settings',
  '/settings/notifications': 'Notifications',
};

function getRouteTitle(pathname: string): string | null {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  if (pathname.startsWith('/project/')) return 'Site Detail';
  if (pathname.startsWith('/charts/')) return 'Site Charts';
  if (pathname.startsWith('/security')) return 'Security';
  return null;
}

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const assistantEnabled = useAssistantEnabled();

  const routeTitle = getRouteTitle(location.pathname);
  const title = routeTitle || tenantName;

  useEffect(() => {
    document.title = routeTitle ? `${routeTitle} — ${tenantName}` : tenantName;
  }, [routeTitle]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleMobileClose = useCallback(() => setMobileOpen(false), []);
  const handleMenuToggle = useCallback(() => setMobileOpen((prev) => !prev), []);

  return (
    <div className="flex h-screen">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={handleMobileClose}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-ems-bg">
        <TopBar title={title} onMenuToggle={handleMenuToggle} />
        <AlertBanner />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
        <PoweredByFooter />
      </div>
      {/* Only for tenants with the assistant switched on (#860) — this also removes ⌘K and "Ask AI". */}
      {assistantEnabled && <AssistantWidget />}
    </div>
  );
}
